import { Menu } from "lucide-react";
import { useState } from "react";
import { HelpTip } from "@/components/HelpTip.js";
import { ThemeToggle } from "@/components/ThemeToggle.js";
import { Button } from "@/components/ui/button.js";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog.js";
import { Separator } from "@/components/ui/separator.js";
import { IPFilterInput } from "@/features/ip-filter/IPFilterInput.js";
import { tooltips } from "@/copy/tooltips.js";
import { getSiteHost } from "@/lib/site.js";
import { cn } from "@/lib/utils.js";
import { SidebarNav } from "./SidebarNav.js";

type Props = {
  onRefresh: () => void;
  onLogout: () => void;
  onOpenWhitelist: () => void;
  onOpenBan: () => void;
};

function SiteHostBadge({ className }: { className?: string }) {
  const host = getSiteHost();
  if (!host) return null;
  return (
    <HelpTip content={tooltips.siteHost}>
      <span
        tabIndex={0}
        className={cn(
          "max-w-[min(100%,14rem)] truncate rounded-md border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground",
          className
        )}
      >
        {host}
      </span>
    </HelpTip>
  );
}

export function TopBar({ onRefresh, onLogout, onOpenWhitelist, onOpenBan }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 flex flex-col gap-3 border-b bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:h-14 md:flex-row md:items-center md:gap-4 md:px-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="md:hidden">
          <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Open navigation menu">
                <Menu className="size-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="fixed left-0 top-0 z-50 flex h-full max-w-[280px] translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-r p-0 data-[state=closed]:slide-out-to-left-0 data-[state=open]:slide-in-from-left-0 sm:max-w-[280px]">
              <DialogTitle className="sr-only">Navigation</DialogTitle>
              <div className="border-b p-4 text-lg font-semibold">firewall-console</div>
              <SidebarNav onNavigate={() => setMobileOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="hidden min-w-0 items-center gap-2 md:flex">
          <span className="text-lg font-semibold tracking-tight">firewall-console</span>
          <SiteHostBadge />
        </div>

        <SiteHostBadge className="md:hidden" />
      </div>

      <div className="min-w-0 flex-1">
        <IPFilterInput />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <HelpTip content={tooltips.refresh}>
          <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
        </HelpTip>
        <HelpTip content={tooltips.whitelistIp}>
          <Button type="button" variant="secondary" size="sm" onClick={onOpenWhitelist}>
            Whitelist IP
          </Button>
        </HelpTip>
        <HelpTip content={tooltips.banIp}>
          <Button type="button" variant="outline" size="sm" onClick={onOpenBan}>
            Ban IP…
          </Button>
        </HelpTip>
        <Separator orientation="vertical" className="hidden h-6 sm:block" />
        <ThemeToggle />
        <HelpTip content={tooltips.logout}>
          <Button type="button" variant="ghost" size="sm" onClick={onLogout}>
            Log out
          </Button>
        </HelpTip>
      </div>
    </header>
  );
}
